// Typed client for the appointment-book endpoints the booking panel uses.
// Same paths online (bff-business) and on a node (erp-core localbook).
//
//   GET  /v1/business/customers?q=                     → CustomersResponse
//   GET  /v1/business/services                          → ServicesResponse
//   GET  /v1/business/scheduling/effective?service_id=  → EffectiveResponse
//   GET  /v1/business/slots?date=&service_id=&staff_id?&grid=1 → SlotsResponse
//   POST /v1/business/bookings (+ Idempotency-Key)      → 201 Appointment
//        409 slot_taken | no_staff | staff_not_qualified, 422 booking_beyond_advance_window,
//        400 missing_customer_name | invalid_source | …, 404 not_found
//
// `grid=1` anchors the slot day at Baghdad midnight on a node (localbook
// open question 1); the BFF drops unknown params, so it is harmless online.

import { useQuery } from '@tanstack/react-query';
import { businessApi } from '@/lib/api';
import type { Appointment, CreateBookingBody, CustomersResponse, EffectiveResponse, ServicesResponse, SlotsResponse } from './types';

export const bookKeys = {
  all: ['business'] as const,
  customers: (q: string) => ['business', 'customers', q] as const,
  services: () => ['business', 'services'] as const,
  effective: (serviceId: string) => ['business', 'scheduling', 'effective', serviceId] as const,
  slots: (date: string, serviceId: string, staffId: string) => ['business', 'slots', date, serviceId, staffId] as const,
};

export const bookApi = {
  customers: (q: string) => businessApi.get<CustomersResponse>('/v1/business/customers', { q: q || undefined }),
  services: () => businessApi.get<ServicesResponse>('/v1/business/services'),
  effective: (serviceId: string) => businessApi.get<EffectiveResponse>('/v1/business/scheduling/effective', { service_id: serviceId }),
  slots: (date: string, serviceId: string, staffId?: string) =>
    businessApi.get<SlotsResponse>('/v1/business/slots', { date, service_id: serviceId, staff_id: staffId || undefined, grid: 1 }),
  createBooking: (body: CreateBookingBody, idempotencyKey: string) =>
    businessApi.post<Appointment>('/v1/business/bookings', body, { headers: { 'Idempotency-Key': idempotencyKey } }),
};

/** A fresh key per confirm attempt (a retry after a network failure reuses it). */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `bk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useCustomerSearch(q: string, enabled: boolean) {
  return useQuery({
    queryKey: bookKeys.customers(q),
    queryFn: () => bookApi.customers(q),
    enabled: enabled && q.trim().length > 0,
    staleTime: 30_000,
    retry: 0,
  });
}

/** The whole book (no q) — used to resolve a `?customer=<id>` prefill. */
export function useCustomerBook(enabled: boolean) {
  return useQuery({
    queryKey: bookKeys.customers(''),
    queryFn: () => bookApi.customers(''),
    enabled,
    staleTime: 30_000,
    retry: 0,
  });
}

export function useServices(enabled = true) {
  return useQuery({ queryKey: bookKeys.services(), queryFn: bookApi.services, enabled, staleTime: 60_000, retry: 0 });
}

export function useEffective(serviceId: string | null) {
  return useQuery({
    queryKey: bookKeys.effective(serviceId ?? ''),
    queryFn: () => bookApi.effective(serviceId as string),
    enabled: !!serviceId,
    staleTime: 30_000,
    retry: 0,
  });
}

export function useSlots(date: string | null, serviceId: string | null, staffId: string) {
  return useQuery({
    queryKey: bookKeys.slots(date ?? '', serviceId ?? '', staffId),
    queryFn: () => bookApi.slots(date as string, serviceId as string, staffId),
    enabled: !!date && !!serviceId,
    staleTime: 0,
    retry: 0,
  });
}
